/**
 * @import { Position } from 'geojson'
 * @import { RouteFeature } from '../types/route-feature'
 */

import { degreesPerPixel } from '@/utils/map';
import { useMap } from '@vis.gl/react-google-maps';
import { useMemo } from 'react';
import { usePolyline } from '../hooks/use-polyline';
import { useShowArrow } from '../hooks/use-show-arrow';
import { highDetailMinZoom, useZoom } from '@/hooks/use-zoom';
import { createArrowIconSequence } from '../utils/icon';
import { emptyLineString } from '../utils/line-string';
import { zIndex } from '@/features/itinerary-map/utils/layer';

const lineColor = '#b10a32';

/** @type {google.maps.PolylineOptions} */
const polylineOptions = { strokeColor: lineColor, strokeWeight: 4, zIndex: zIndex.riverPath, clickable: false };

/**
 * @param {object} props
 * @param {RouteFeature} props.feature
 * @returns {React.JSX.Element}
 */
export function RiverPolyline({ feature }) {
  const map = useMap();
  const zoom = useZoom();
  const showArrow = useShowArrow(zoom, feature);

  const arrowFeature = useMemo(() => {
    // Adjusting the zoom we use to more aggressively smooth the line out for the purpose of getting a heading
    const dpp = degreesPerPixel(Math.max(0, zoom - 3));

    /** @type {number[]} */
    let lastPosition = feature.geometry.coordinates[0];
    const filteredCoordinates = lastPosition
      ? [lastPosition].concat(
          feature.geometry.coordinates.filter((position) => {
            const distanceDegrees = Math.sqrt(
              Math.pow(position[0] - lastPosition[0], 2) + Math.pow(position[1] - lastPosition[1], 2)
            );
            if (distanceDegrees < dpp) {
              return false;
            }

            lastPosition = position;
            return true;
          })
        )
      : [];
    if (feature.geometry.coordinates.length) {
      filteredCoordinates.push(/** @type {Position} */ (feature.geometry.coordinates.at(-1)));
    }

    return {
      ...feature,
      geometry: {
        ...feature.geometry,
        coordinates: filteredCoordinates,
      },
    };
  }, [feature, zoom]);

  const arrowPolylineOptions = useMemo(() => {
    if (!map) {
      return {};
    }

    return /** @type {google.maps.PolylineOptions} */ ({
      zIndex: zIndex.riverArrowPath,
      strokeColor: 'transparent',
      icons: createArrowIconSequence(lineColor, highDetailMinZoom, zoom),
      clickable: false,
    });
  }, [map, zoom]);

  // Arrows use a separate, simplified line to reduce the chance of their placement at odd angles for winding
  // rivers paths.
  usePolyline(showArrow ? arrowFeature : emptyLineString, arrowPolylineOptions);

  usePolyline(zoom >= highDetailMinZoom ? feature : arrowFeature, polylineOptions);

  return <></>;
}
